/**
 * Nhóm đối tượng "Sinh viên" lấy từ UIS (`UIS_LoaiHinhDaoTao`) thay cho các
 * node `student*` khai trong bảng loại thủ tục — bật/tắt bằng
 * `BUSINESS_CONFIG.student_procedure_type_source`.
 *
 * Khi bật (`'uis'`):
 *  - Mỗi loại hình đào tạo UIS = 1 node con `ValueStr = student_<StudyTypeID>`
 *    nằm dưới node nhãn nhóm "Sinh viên" (`ValueStr` rỗng), đứng ĐẦU cây.
 *  - Mọi node `student*` trong DB bị ẩn (cùng các nhãn nhóm chỉ chứa chúng).
 *  - Sinh viên khớp node khi `StudentInfo.StudyTypeID` trùng `<StudyTypeID>`
 *    (xem `isProcedureTypeEligibleForUser`).
 *
 * Token `student_<StudyTypeID>` cũng là giá trị lưu vào `Types_Token` của thủ
 * tục (admin tick ở `procedure-type-selector.tsx`).
 */

import { BUSINESS_CONFIG } from '@/config/business.config';
import { ProcedureTypeStartingCodes } from '@/types/enum';
import { ProcedureTypeResponse } from './type';

/** 1 phần tử response của executor `UIS_LoaiHinhDaoTao`. */
export interface IUisLoaiHinhDaoTaoResponse {
    StudyTypeID: string;
    StudyTypeName: string;
}

/** Cờ: nhóm "Sinh viên" lấy từ UIS? */
export const isUisStudentProcedureTypeEnabled = (): boolean =>
    BUSINESS_CONFIG.student_procedure_type_source === 'uis';

/** Prefix token loại hình UIS — vd `student_` + `01` ⇒ `student_01`. */
const UIS_STUDENT_TOKEN_PREFIX = `${ProcedureTypeStartingCodes.Student}_`;

const UIS_STUDENT_ROOT_ID = 'uis-student-root';

/** `StudyTypeID` → token lưu ở `ValueStr` / `Types_Token`. */
export const buildUisStudentToken = (studyTypeId: string): string =>
    `${UIS_STUDENT_TOKEN_PREFIX}${studyTypeId.trim()}`;

/** `ValueStr` có thuộc nhóm sinh viên (`student*`) không. */
export const isStudentToken = (valueStr: string | null | undefined): boolean =>
    !!valueStr &&
    valueStr
        .trim()
        .toLowerCase()
        .startsWith(ProcedureTypeStartingCodes.Student);

/** Lọc response UIS hợp lệ (bỏ phần tử thiếu `StudyTypeID`). */
export const parseUisLoaiHinhDaoTao = (
    data: unknown
): IUisLoaiHinhDaoTaoResponse[] =>
    Array.isArray(data)
        ? data.filter(
              (x): x is IUisLoaiHinhDaoTaoResponse =>
                  !!x &&
                  typeof x.StudyTypeID === 'string' &&
                  !!x.StudyTypeID.trim()
          )
        : [];

const buildUisStudentChildren = (
    studyTypes: IUisLoaiHinhDaoTaoResponse[]
): ProcedureTypeResponse[] =>
    studyTypes.map((x, index) => ({
        ID: `uis-student-${x.StudyTypeID.trim()}`,
        Name: x.StudyTypeName,
        ValueStr: buildUisStudentToken(x.StudyTypeID),
        IsShowUi: true,
        Aliases_Token: '',
        RegexBuilderJson: null,
        IsParent: false,
        ParentId: UIS_STUDENT_ROOT_ID,
        ParentIds: UIS_STUDENT_ROOT_ID,
        NumberOfParent: 1,
        Ordinal: index,
        Childrens: [],
    }));

const buildUisStudentRoot = (
    studentLabel: string,
    childrens: ProcedureTypeResponse[]
): ProcedureTypeResponse => ({
    ID: UIS_STUDENT_ROOT_ID,
    Name: studentLabel,
    ValueStr: '',
    IsShowUi: true,
    Aliases_Token: '',
    RegexBuilderJson: null,
    IsParent: true,
    ParentId: null,
    ParentIds: '',
    NumberOfParent: 0,
    Ordinal: -1,
    Childrens: childrens,
});

/**
 * Bỏ node `student*` khỏi cây DB. Nhãn nhóm (`ValueStr` rỗng) vốn có con mà
 * sau khi lọc hết con ⇒ bỏ luôn (vd nhãn "Sinh viên" cũ).
 */
const removeStudentNodesFromTree = (
    tree: ProcedureTypeResponse[]
): ProcedureTypeResponse[] =>
    tree.flatMap((node) => {
        if (isStudentToken(node.ValueStr)) return [];
        const hadChildren = !!node.Childrens?.length;
        const childrens = hadChildren
            ? removeStudentNodesFromTree(node.Childrens!)
            : node.Childrens;
        if (hadChildren && !childrens?.length && !node.ValueStr?.trim())
            return [];
        return [{ ...node, Childrens: childrens }];
    });

/** Như [[removeStudentNodesFromTree]] nhưng cho danh sách phẳng (theo `ParentId`). */
const removeStudentNodesFromList = (
    list: ProcedureTypeResponse[]
): ProcedureTypeResponse[] => {
    let result = list.filter((x) => !isStudentToken(x.ValueStr));
    const originalParentIds = new Set(
        list.map((x) => x.ParentId).filter(Boolean)
    );
    // Lặp tới khi ổn định để bỏ được nhãn nhóm lồng nhiều cấp.
    for (;;) {
        const currentParentIds = new Set(
            result.map((x) => x.ParentId).filter(Boolean)
        );
        const next = result.filter(
            (x) =>
                !!x.ValueStr?.trim() ||
                !x.ID ||
                !originalParentIds.has(x.ID) ||
                currentParentIds.has(x.ID)
        );
        if (next.length === result.length) return result;
        result = next;
    }
};

/**
 * Cây loại thủ tục sau khi áp cờ UIS: ẩn node `student*` của DB, chèn nhóm
 * "Sinh viên" từ UIS lên đầu. UIS rỗng / lỗi ⇒ không có nhóm Sinh viên.
 */
export const mergeUisStudentIntoTree = (
    tree: ProcedureTypeResponse[],
    studyTypes: IUisLoaiHinhDaoTaoResponse[],
    studentLabel: string
): ProcedureTypeResponse[] => {
    const rest = removeStudentNodesFromTree(tree);
    if (!studyTypes.length) return rest;
    return [
        buildUisStudentRoot(
            studentLabel,
            buildUisStudentChildren(studyTypes)
        ),
        ...rest,
    ];
};

/** Như [[mergeUisStudentIntoTree]] cho danh sách phẳng. */
export const mergeUisStudentIntoList = (
    list: ProcedureTypeResponse[],
    studyTypes: IUisLoaiHinhDaoTaoResponse[],
    studentLabel: string
): ProcedureTypeResponse[] => {
    const rest = removeStudentNodesFromList(list);
    if (!studyTypes.length) return rest;
    return [
        buildUisStudentRoot(studentLabel, []),
        ...buildUisStudentChildren(studyTypes),
        ...rest,
    ];
};
